// Macro-widgets, rendered as a DOM overlay (Set 12.5). Eric's call: instead of drawing the plugin UI on
// the game canvas, mount it as real elements ON TOP of the canvas. The compose model + the Lua API
// (ui.modal/vstack/icon_grid) + the plugin's client.lua are UNCHANGED — this just swaps the engine's
// render+input adapter from canvas-draw + hit-test to DOM elements + native events. See the "macro-
// widgets" artifact.
//
// Input passthrough: the overlay root is `pointer-events: none`, and only the interactive bits (cells,
// the modal dim/panel) are `pointer-events: auto`. So gameplay mouse (move / aim / fire) falls THROUGH
// to the canvas everywhere except on an actual widget — the same coexistence the settings/wizard/console
// overlays already rely on. Keyboard (Esc to close, the open key) stays in InputService.
//
// Layout is CSS (flex for stacks, grid for icon_grid), so the browser lays out + hit-tests natively —
// no measure/place, no cell math. Icons are the game's own pickupIcon art, rendered once to a data URL.

import { drawItemIcon } from "../render/sprites.js";

const CELL = 46; // px per slot — matches the old canvas SLOT so the look is unchanged
const CCHIP = 44, CCHIP_STRIDE = 54; // carousel chip width + centre-to-centre stride (chip + gap)

export class WidgetLayer {
    constructor(doc = document) {
        this.doc = doc;
        this.root = doc.createElement("div");
        this.root.className = "wl-root";
        if (doc.body && doc.body.appendChild) doc.body.appendChild(this.root);
        this._sig = null;         // last-rendered signature — skip rebuilding when nothing changed
        this._icons = new Map();  // item -> icon data URL (pickupIcon rendered once)
        this._manifest = {};      // last manifest, for the drag ghost's icon
        this._drag = null; this._ghost = null; // in-flight drag (Option B) + its cursor-following icon
        // Drag move/up ride the document so a drag can leave the cell it started on (guarded for tests).
        if (doc.addEventListener) {
            doc.addEventListener("pointermove", (e) => this._onDragMove(e));
            doc.addEventListener("pointerup", (e) => this._onDragUp(e));
        }
    }

    // Reconcile the overlay to the plugin's current widget tree. Cheap when unchanged: it rebuilds the DOM
    // only when the visible widgets or their cell contents change (hover is pure CSS, so it never rebuilds).
    sync(vm, manifest) {
        this._manifest = manifest || {};
        const modal = vm && vm.openModal ? vm.openModal() : null;
        const mounts = vm && vm.hudMounts ? vm.hudMounts() : [];
        const grids = [], carousels = [];
        const collect = (w) => { if (w.type === "grid") grids.push(w); else if (w.type === "carousel") carousels.push(w); w.children.forEach(collect); };
        for (const m of mounts) collect(m.widget);
        if (modal) collect(modal);
        const cellData = new Map(grids.map((g) => [g, safeCells(g)])); // pull once (calls Lua) → sig + build share it
        const carData = new Map(carousels.map((c) => [c, safeCarousel(c)]));
        this._selData = new Map(grids.map((g) => [g, safeSelected(g)])); // the item each grid highlights (held item)
        // The carousel's slide (selected) + cooldown (ready) animate WITHOUT a rebuild, so only its item
        // STRIP (names/counts) is in the signature; selected/ready are applied imperatively below every frame.
        // The grid's highlighted-item IS in the signature — a selection change rebuilds so the highlight moves.
        const sig = signature(modal, mounts, cellData, carData, this._selData);
        if (sig !== this._sig) { this._sig = sig; this._render(modal, mounts, manifest, cellData, carData); }
        this._updateCarousels(); // every frame: slide the strip to the selection + draw the cooldown sweep
    }

    _render(modal, mounts, manifest, cellData, carData) {
        this.root.replaceChildren();
        for (const m of mounts) this.root.appendChild(this._widgetEl(m.widget, manifest, cellData, m.anchor, carData));
        if (modal) this.root.appendChild(this._modalEl(modal, manifest, cellData));
    }

    _modalEl(modal, manifest, cellData) {
        const dim = this.doc.createElement("div"); dim.className = "wl-modal";
        const panel = this.doc.createElement("div"); panel.className = "wl-panel";
        const title = this.doc.createElement("div"); title.className = "wl-title";
        title.textContent = modal.props.title || "";
        const hint = this.doc.createElement("span"); hint.className = "wl-hint"; hint.textContent = "Esc to close"; title.appendChild(hint);
        panel.appendChild(title);
        for (const c of modal.children) panel.appendChild(this._widgetEl(c, manifest, cellData));
        dim.appendChild(panel);
        return dim;
    }

    // Build the element for a widget subtree; `anchor` (mounts only) pins it to a screen edge/corner.
    _widgetEl(w, manifest, cellData, anchor, carData) {
        let el;
        if (w.type === "grid") el = this._gridEl(w, manifest, cellData);
        else if (w.type === "carousel") el = this._carouselEl(w, manifest, (carData && carData.get(w)) || safeCarousel(w));
        else if (w.type === "text") { el = this.doc.createElement("div"); el.className = "wl-text"; el.textContent = w.props.text || ""; }
        else {
            el = this.doc.createElement("div");
            el.className = w.type === "hstack" ? "wl-hstack" : "wl-vstack";
            el.style.gap = (typeof w.props.gap === "number" ? w.props.gap : 14) + "px";
            for (const c of w.children) el.appendChild(this._widgetEl(c, manifest, cellData, undefined, carData));
        }
        if (anchor) el.classList.add("wl-anchor-" + anchor);
        return el;
    }

    // The shooter weapon carousel (Set 12.9): a horizontal strip of weapon chips, the selected one slid to
    // dead-centre. The STRIP (which chips, their counts) is built here and rebuilt only when it changes; the
    // SLIDE + the cooldown sweep are applied every frame by _updateCarousels on the persistent track, so the
    // browser transitions the transform (a smooth slide) and the sweep ticks without churning the DOM.
    _carouselEl(w, manifest, state) {
        const doc = this.doc;
        const wrap = doc.createElement("div"); wrap.className = "wl-carousel"; wrap._widget = w;
        const name = doc.createElement("div"); name.className = "wl-cname"; wrap.appendChild(name); wrap._name = name;
        const view = doc.createElement("div"); view.className = "wl-cview";
        const track = doc.createElement("div"); track.className = "wl-ctrack"; wrap._track = track;
        const chips = []; wrap._chips = chips;
        for (const it of (state.items || [])) {
            const chip = doc.createElement("div"); chip.className = "wl-cchip";
            const img = doc.createElement("img"); img.className = "wl-icon"; img.draggable = false; img.src = this._icon(it.item, manifest); chip.appendChild(img);
            if (it.swatch) { const sw = doc.createElement("span"); sw.className = "wl-cswatch"; sw.style.background = it.swatch; chip.appendChild(sw); }
            const cnt = doc.createElement("span"); cnt.className = "wl-count"; cnt.textContent = it.infinite ? "∞" : String(it.count); chip.appendChild(cnt);
            const cool = doc.createElement("div"); cool.className = "wl-ccool"; chip.appendChild(cool); chip._cool = cool;
            // A usage/durability bar along the chip's bottom (Set 19), for a held item that wears out (the
            // paint gun's canister). Hidden unless the strip item carries `uses`; _updateCarousels drives it.
            const usage = doc.createElement("div"); usage.className = "wl-usage";
            const ufill = doc.createElement("div"); ufill.className = "wl-usage-fill"; usage.appendChild(ufill);
            chip.appendChild(usage); chip._usage = usage; chip._ufill = ufill;
            track.appendChild(chip); chips.push(chip);
        }
        view.appendChild(track); wrap.appendChild(view);
        return wrap;
    }

    // Slide every mounted carousel to its current selection and draw the cooldown sweep — imperatively, on
    // the persistent elements, so the CSS transition animates the slide and the sweep updates every frame
    // without a rebuild. Reads fresh state per carousel (the selection + cooldown change constantly).
    _updateCarousels() {
        const list = this.root.querySelectorAll ? this.root.querySelectorAll(".wl-carousel") : [];
        for (const wrap of list) {
            const st = safeCarousel(wrap._widget);
            const chips = wrap._chips || [];
            const sel = Math.max(0, Math.min(chips.length - 1, st.selected | 0));
            const ready = typeof st.ready === "number" ? st.ready : 1;
            // Centre the selected chip: shift the track left by the selected chip's centre offset. left:50%
            // puts the track origin at the view centre, so chip[sel]'s centre lands dead-centre.
            wrap._track.style.transform = `translateX(${-(sel * CCHIP_STRIDE + CCHIP / 2)}px)`;
            for (let i = 0; i < chips.length; i++) {
                const on = i === sel;
                chips[i].classList.toggle("wl-sel", on);
                // The sweep darkens the bottom (1-ready) of the SELECTED chip and recedes as it recharges.
                if (chips[i]._cool) chips[i]._cool.style.height = on ? `${Math.round((1 - ready) * 100)}%` : "0%";
                // The usage bar (Set 19): shown for any chip whose item wears out, so you can watch the
                // paint gun's charges drain even before it reaches your hand as the selected chip.
                const it = st.items && st.items[i];
                if (chips[i]._usage) {
                    if (it && it.uses > 0) {
                        const frac = Math.max(0, Math.min(1, (it.count || 0) / it.uses));
                        chips[i]._usage.style.display = "block";
                        chips[i]._ufill.style.width = `${Math.round(frac * 100)}%`;
                        chips[i]._ufill.style.background = usageColor(frac);
                    } else chips[i]._usage.style.display = "none";
                }
            }
            const label = (st.items && st.items[sel] && st.items[sel].label) || "";
            if (wrap._name.textContent !== label) wrap._name.textContent = label;
        }
    }

    _gridEl(grid, manifest, cellData) {
        const doc = this.doc;
        const wrap = doc.createElement("div");
        wrap.className = "wl-grid" + (gridRows(grid) === 1 ? " wl-hot" : "");
        wrap._grid = grid; // so a drag can find the target grid from the DOM (Set 12.5 Option B)
        const cols = Math.max(1, grid.props.cols || 8);
        wrap.style.gridTemplateColumns = `repeat(${cols}, ${CELL}px)`;
        if (typeof grid.props.gap === "number") wrap.style.gap = grid.props.gap + "px";
        const cells = (cellData && cellData.get(grid)) || safeCells(grid);
        const selName = (this._selData && this._selData.get(grid)) || null; // the held item, highlighted
        const n = cols * gridRows(grid);
        for (let i = 0; i < n; i++) {
            const item = cells[i];
            const el = doc.createElement("div");
            el.className = "wl-cell" + (item ? "" : " wl-empty") + (item && selName && item.item === selName ? " wl-sel" : "");
            el.dataset.i = String(i);
            if (item) {
                // The cell's CONTENT is the plugin's template (ui.icon / ui.label descriptors) — the engine
                // only owns the cell box + the layout. icon_grid lives in a kit plugin (Set 12.5 step 2).
                const content = grid.cellTemplate ? grid.cellTemplate(item, i) : [];
                for (const d of content) this._appendContent(el, d, manifest);
                // Click a filled cell → on_click(i, item), read LIVE (the grid may have changed). A DRAG
                // (Option B) releases on a different cell, so no click fires there — the two don't collide.
                el.addEventListener("click", () => { const live = safeCells(grid)[i] || null; if (grid.onClick && live) grid.onClick(i, live); });
                // Native image-drag + text selection are killed by CSS (user-select/user-drag: none) +
                // img.draggable=false, so OUR ghost is the only drag visual and cells don't "select" — no
                // preventDefault here, which would risk suppressing the click that drops (Eric).
                // A cell is draggable if its grid rearranges (onMove) OR takes part in cross-grid drag (has
                // an id) — so you can lift an item out of a crafting bench back to your inventory, too.
                if (grid.onMove || grid.dropId) el.addEventListener("pointerdown", () => { this._drag = { grid, from: i, item, el }; });
            }
            wrap.appendChild(el);
        }
        return wrap;
    }

    // Drag (Option B): a ghost follows the cursor; on release over a different cell of the same grid we
    // fire on_move(from, to). The server swaps + the new order rides the next snapshot (feels instant).
    _onDragMove(e) {
        if (!this._drag) return;
        if (!this._ghost) {
            // First movement → this is a real drag (not a click): LIFT the item out of its source cell so
            // it isn't shown in two places (the source + the ghost — that was the weirdness Eric spotted),
            // and start the ghost following the cursor.
            if (this._drag.el && this._drag.el.classList) this._drag.el.classList.add("wl-dragging");
            this._ghost = this.doc.createElement("div"); this._ghost.className = "wl-ghost";
            const img = this.doc.createElement("img"); img.className = "wl-icon"; img.draggable = false;
            img.src = this._icon(this._drag.item.item, this._manifest); this._ghost.appendChild(img);
            // carry the stack count on the ghost too, in the same corner as the cell (Eric)
            const cnt = this.doc.createElement("span"); cnt.className = "wl-count"; cnt.textContent = String(this._drag.item.count); this._ghost.appendChild(cnt);
            (this.doc.body || this.root).appendChild(this._ghost);
        }
        this._ghost.style.left = e.clientX + "px"; this._ghost.style.top = e.clientY + "px";
    }
    _onDragUp(e) {
        const d = this._drag; this._drag = null;
        this._removeGhost();
        if (d && d.el && d.el.classList && d.el.classList.remove) d.el.classList.remove("wl-dragging"); // un-lift (the rebuild reflects a successful move)
        if (!d || !this.doc.elementFromPoint) return;
        const cellEl = this.doc.elementFromPoint(e.clientX, e.clientY);
        const cell = cellEl && cellEl.closest ? cellEl.closest(".wl-cell") : null;
        const gridEl = cell && cell.closest ? cell.closest(".wl-grid") : null;
        const target = gridEl && gridEl._grid;
        if (!cell || !target) return;
        const to = +cell.dataset.i;
        if (target === d.grid) {
            if (to !== d.from && d.grid.onMove) d.grid.onMove(d.from, to);   // same grid → rearrange
        } else if (target.onDrop) {
            target.onDrop(d.grid.dropId || "", d.from, to, d.item.item);     // cross-grid → transfer (Set 20.2)
        }
    }
    _removeGhost() { const g = this._ghost; this._ghost = null; if (g) { if (g.remove) g.remove(); else if (g.parentNode) g.parentNode.removeChild(g); } }

    // Render one cell-content descriptor from a grid's cell template: an item icon, or a text label.
    _appendContent(cellEl, d, manifest) {
        if (!d || !d.kind) return;
        if (d.kind === "icon") { const img = this.doc.createElement("img"); img.className = "wl-icon"; img.draggable = false; img.src = this._icon(d.item, manifest); cellEl.appendChild(img); }
        else if (d.kind === "label") { const s = this.doc.createElement("span"); s.className = "wl-count"; s.textContent = String(d.text); cellEl.appendChild(s); }
        // A TIP is the one descriptor that isn't a child node — it's what the cell says on hover, so it
        // sets the box's title rather than appending to it. Native title on purpose: it survives the
        // grid rebuilding under a drag, follows the cursor out of the modal, and costs no layout.
        else if (d.kind === "tip") { if (d.text) cellEl.title = String(d.text); }
        // A BAR (Set 19) is a usage/durability meter along the cell's bottom edge — a track with a fill
        // whose width is the fraction of uses left. Same overlay on a hotbar/grid cell as the carousel
        // chip grows below, so a usage item reads identically wherever it's shown.
        else if (d.kind === "bar") {
            const track = this.doc.createElement("div"); track.className = "wl-usage";
            const fill = this.doc.createElement("div"); fill.className = "wl-usage-fill";
            fill.style.width = `${Math.round(Math.max(0, Math.min(1, +d.frac || 0)) * 100)}%`;
            if (d.color) fill.style.background = String(d.color);
            track.appendChild(fill); cellEl.appendChild(track);
        }
        // A SWATCH (Set 19) fills the cell with a solid colour — the item's manifest tint. The block
        // maker's pixel editor + palette draw with these; an item with no tint leaves the cell empty.
        else if (d.kind === "swatch") {
            const sw = this.doc.createElement("div"); sw.className = "wl-swatch";
            const t = (manifest || {})[d.item];
            sw.style.background = (t && t.tint) || "transparent";
            cellEl.appendChild(sw);
        }
    }

    // The game's own pickup icon as a data URL, rendered once per item (the manifest is stable per item).
    _icon(item, manifest) {
        if (this._icons.has(item)) return this._icons.get(item);
        // An item may carry a KV data suffix ("cookedpasta?cups=6") or an instance token ("mortarpestle@12",
        // Set 21.1) — either way its look comes from the BASE item before the "?" / "@".
        const base = String(item).split(/[?@]/)[0];
        const m = (manifest || {})[item] || (manifest || {})[base] || {};
        const c = this.doc.createElement("canvas"); c.width = 40; c.height = 40;
        const g = c.getContext && c.getContext("2d");
        // drawItemIcon prefers a plugin's own icon() art for this item, else the built-in shape for its key.
        if (g) { g.translate(20, 20); g.scale(1.35, 1.35); drawItemIcon(g, base, m.icon, m.tint); }
        const url = c.toDataURL ? c.toDataURL() : "";
        this._icons.set(item, url);
        return url;
    }
}

function safeCells(g) { try { const c = g.cells ? g.cells() : []; return Array.isArray(c) ? c : []; } catch { return []; } }
function safeSelected(g) { try { return (g.selected ? g.selected() : null) || null; } catch { return null; } }
function gridRows(g) { return Math.max(1, g.props.rows || 1); }
// A usage bar's colour by how full it is (Set 19): green when fresh, amber, red as it's about to break —
// the same read-at-a-glance cue a health bar gives. Used by the carousel chip; the grid cell takes the
// colour the plugin passed to ui.bar, but they agree because the plugin uses this same green→red idea.
function usageColor(frac) { return frac > 0.5 ? "#7bd88f" : frac > 0.2 ? "#ffcc4d" : "#ff5b6e"; }
// The carousel's live state: the weapon strip + which is selected + the cooldown ready-fraction. Only the
// strip is structural (below); selected/ready animate imperatively (they must not trigger a DOM rebuild).
function safeCarousel(c) {
    try { const s = c.carouselState ? c.carouselState() : null; return s && Array.isArray(s.items) ? s : { items: [], selected: 0, ready: 1 }; }
    catch { return { items: [], selected: 0, ready: 1 }; }
}
// The chips that exist + their counts/swatches — NOT selected/ready, so a weapon switch or a cooldown tick
// slides/sweeps without rebuilding the strip (only firing/switching what you HOLD rebuilds it).
function carStrip(s) { return (s.items || []).map((i) => `${i.item}:${i.infinite ? "∞" : i.count}${i.swatch || ""}${i.uses ? "/" + i.uses : ""}`).join(","); }
function signature(modal, mounts, cellData, carData, selData) {
    const parts = [modal ? "M" + modal.id + (modal.props.title || "") : "-"];
    for (const m of mounts) parts.push("H" + m.widget.id + m.anchor);
    for (const [g, cells] of cellData) parts.push(g.id + ":" + (g.props.cols || 8) + "x" + gridRows(g) + ":" + JSON.stringify(cells) + ":sel=" + ((selData && selData.get(g)) || ""));
    if (carData) for (const [c, st] of carData) parts.push("C" + c.id + ":" + carStrip(st));
    return parts.join("|");
}
